import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/UI/Input';
import { Select } from '@/components/UI/Select';
import { Textarea } from '@/components/UI/Textarea';
import { Switch } from '@/components/UI/Switch';
import { Modal } from '@/components/UI/Modal';
import { Alert } from '@/components/UI/Alert';
import { Badge } from '@/components/UI/Badge';
import { apiErrorMessage, getErrorDetail } from '@/utils/apiError';
import { useUpdateToolErrorMutation, type ToolError } from '@/redux/internal-services/toolErrorsApi';
import { messageProblem, type MessageProblem } from './toolErrorMessage';

/** Retryable choice on an error: inherit the type's default, or force yes / no. */
type RetryChoice = 'inherit' | 'true' | 'false';

const toChoice = (v: boolean | null): RetryChoice => (v === null ? 'inherit' : v ? 'true' : 'false');
const fromChoice = (c: RetryChoice): boolean | null => (c === 'inherit' ? null : c === 'true');

interface Props {
  error: ToolError;
  /** Default `retryable` per error type, for the "Type default (Yes/No)" option. */
  typeDefault: Record<string, boolean>;
  /** A success reply: it has no retry rule, so the Retryable field is hidden and never sent. */
  isSuccess?: boolean;
  onClose: () => void;
  /** Shown above the form, e.g. that the change applies to every agent using the tool. */
  note?: string;
}

/**
 * Edit one SAM tool error (message, retryable, description, active) in the shared catalogue.
 * Used by the Tool Errors page and the Agent page's tool panel.
 */
export const ToolErrorEditDialog: React.FC<Props> = ({ error, typeDefault, isSuccess = false, onClose, note }) => {
  const { t } = useTranslation();
  const [updateError, { isLoading: saving }] = useUpdateToolErrorMutation();
  const [draft, setDraft] = useState({ ...error, retry: toChoice(error.retryable) });
  const [formError, setFormError] = useState<string | null>(null);

  const variables = draft.variables ?? [];
  const problem = messageProblem(draft.message, variables);
  const problemText = (p: MessageProblem | null) => {
    if (!p) return undefined;
    if (p.kind === 'empty') return t('platform.toolErrors.messageEmpty');
    if (p.kind === 'unbalanced') return t('platform.toolErrors.messageUnbalanced');
    return t('platform.toolErrors.messageUnknown', {
      names: p.names.join(', '),
      allowed: variables.length ? variables.join(', ') : t('platform.toolErrors.noVariables'),
    });
  };
  const yesNo = (v: boolean | undefined) => (v ? t('platform.toolErrors.yes') : t('platform.toolErrors.no'));

  const save = async () => {
    setFormError(null);
    try {
      await updateError({
        code: draft.code, message: draft.message, description: draft.description ?? null,
        ...(isSuccess ? {} : { retryable: fromChoice(draft.retry) }), is_active: draft.is_active,
      }).unwrap();
      onClose();
    } catch (e) {
      const detail = getErrorDetail(e);
      const msg = apiErrorMessage(e, t, t('platform.toolErrors.saveFailed'));
      setFormError(detail && detail !== msg ? `${msg} ${detail}` : msg);
    }
  };

  return (
    <Modal
      title={draft.name}
      onClose={onClose}
      onSave={save}
      saving={saving}
      saveDisabled={!!problem}
      saveLabel={t('platform.toolErrors.save')}
      closeOnBackdrop={false}
    >
      <div className="space-y-3">
        {note && <Alert tone="info">{note}</Alert>}
        {formError && <Alert tone="danger">{formError}</Alert>}
        <div className="grid grid-cols-2 gap-3">
          <Input label={t('platform.toolErrors.code')} value={draft.code} readOnly />
          <Input
            label={t('platform.toolErrors.type')}
            value={draft.type}
            readOnly
            hint={draft.type_from_exception ? t('platform.toolErrors.typeFromExceptionHint') : undefined}
          />
        </div>
        {draft.message_edited && <p className="text-caption text-ui-text3">{t('platform.toolErrors.messageEdited')}</p>}
        <div>
          <p className="text-caption text-ui-text2">{t(isSuccess ? 'platform.toolErrors.returnedBy' : 'platform.toolErrors.raisedBy')}</p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {(draft.tools ?? []).map((name) => <Badge key={name} variant="neutral" className="font-mono">{name}</Badge>)}
          </div>
        </div>
        <Textarea
          label={t('platform.toolErrors.message')}
          hint={problem ? undefined : t('platform.toolErrors.messageHint', {
            example: `{{ ${variables[0] ?? 'name'} }}`, interpolation: { escapeValue: false },
          })}
          error={problemText(problem)}
          value={draft.message}
          onChange={(e) => setDraft({ ...draft, message: e.target.value })}
        />
        <div>
          <p className="text-caption text-ui-text2">{t('platform.toolErrors.availableVariables')}</p>
          {variables.length ? (
            <div className="mt-1 flex flex-wrap gap-1.5">
              {variables.map((v) => <Badge key={v} variant="neutral" className="font-mono">{`{{ ${v} }}`}</Badge>)}
            </div>
          ) : (
            <p className="mt-1 text-caption text-ui-text3">{t('platform.toolErrors.noVariables')}</p>
          )}
        </div>
        {!isSuccess && (
          <Select
            label={t('platform.toolErrors.retryable')}
            value={draft.retry}
            onChange={(e) => setDraft({ ...draft, retry: e.target.value as RetryChoice })}
          >
            <option value="inherit">{t('platform.toolErrors.inherits', { value: yesNo(typeDefault[draft.type]) })}</option>
            <option value="true">{t('platform.toolErrors.yes')}</option>
            <option value="false">{t('platform.toolErrors.no')}</option>
          </Select>
        )}
        <Textarea
          label={t('platform.toolErrors.description')}
          value={draft.description ?? ''}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
        />
        <div className="flex items-center justify-between gap-3">
          <span id="error-active" className="text-sm text-ui-text">{t('platform.toolErrors.active')}</span>
          <Switch
            aria-labelledby="error-active"
            checked={draft.is_active}
            onCheckedChange={(next) => setDraft({ ...draft, is_active: next })}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ToolErrorEditDialog;
